import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Button, Stack } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import QRCode from 'qrcode';

interface QrisQrDisplayProps {
  qrString: string;
  amount?: number;
  title?: string;
  downloadFileName?: string;
}

const QrisQrDisplay: React.FC<QrisQrDisplayProps> = ({
  qrString,
  amount,
  title = 'Scan QRIS untuk Bayar',
  downloadFileName,
}) => {
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setQrImageUrl(null);
    setQrError('');

    QRCode.toDataURL(qrString, { width: 220, margin: 2, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setQrImageUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrError('Gagal membuat kode QR');
      });

    return () => {
      cancelled = true;
    };
  }, [qrString]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(value);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const url = await QRCode.toDataURL(qrString, {
        width: 512,
        margin: 2,
        errorCorrectionLevel: 'M',
      });
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName || 'qris-pembayaran.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setQrError('Gagal mengunduh kode QR');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        textAlign: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      {amount != null && (
        <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
          {formatCurrency(amount)}
        </Typography>
      )}
      <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            p: 2,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid #eee',
            minHeight: 220,
            minWidth: 220,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {qrError ? (
            <Typography color="error" variant="body2">{qrError}</Typography>
          ) : qrImageUrl ? (
            <img src={qrImageUrl} alt="QRIS PayBOS" width={220} height={220} />
          ) : (
            <CircularProgress size={32} />
          )}
        </Box>
        {qrImageUrl && !qrError && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
            fullWidth
            sx={{ maxWidth: 280 }}
          >
            {downloading ? 'Mengunduh...' : 'Download QR'}
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Buka aplikasi e-wallet atau mobile banking, pilih Scan QRIS, lalu arahkan ke kode di atas.
      </Typography>
    </Paper>
  );
};

export default QrisQrDisplay;
